//based on Downshift Combobox example: https://www.downshift-js.com/use-combobox

import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCombobox } from 'downshift';
import type { MetricIndexEntry } from '@/types';

type Metric = MetricIndexEntry;

export default function MetricCombo({
  items: initialItems,
  setMetricKey,
  metricKey,
  recommendedMetricKeys,
}: {
  items: Metric[];
  setMetricKey: (key: string) => void;
  metricKey?: string;
  recommendedMetricKeys?: string[] | null;
}) {
  const [items, setItems] = useState<Metric[]>(initialItems || []);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<{
    top: number;
    left: number;
    width: number;
    maxWidth?: number;
  } | null>(null);

  // Organize items into recommended and other sections
  const organizeItems = (allItems: Metric[]) => {
    const recommended = allItems.filter((m) =>
      recommendedMetricKeys?.some((key) => m.id.toLowerCase() === key.toLowerCase()),
    );
    const other = allItems.filter(
      (m) => !recommendedMetricKeys?.some((key) => m.id.toLowerCase() === key.toLowerCase()),
    );
    // Return flat array with recommended first for downshift to manage
    return [...recommended, ...other];
  };

  useEffect(() => {
    setItems(organizeItems(initialItems || []));
  }, [initialItems, recommendedMetricKeys]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
  } = useCombobox<Metric>({
    items,
    itemToString: (item) => (item ? item.title || '(untitled)' : ''),
    onInputValueChange: ({ inputValue }) => {
      const iv = inputValue || '';
      const lower = iv.toLowerCase().trim();
      const filtered = (initialItems || []).filter((m) => {
        return (
          !lower ||
          (m.id && m.id.toLowerCase().includes(lower)) ||
          (m.title && m.title.toLowerCase().replace(/_/g, ' ').includes(lower))
        );
      });
      setItems(organizeItems(filtered));
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) setMetricKey(selectedItem.id);
    },
  });

  useEffect(() => {
    function update() {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = rect.width;
      setMenuStyle({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width,
        maxWidth: width,
      });
    }

    if (isOpen) update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, items.length]);

  useEffect(() => {
    if (!metricKey) return;
    const found = (initialItems || []).find((m) => m.id === metricKey);
    if (found) setInputValue(found.title || '(untitled)');
  }, [metricKey, initialItems, setInputValue]);

  const menu = (
    <ul
      {...getMenuProps()}
      className={`list-unstyled position-absolute bg-white shadow-sm mt-1 p-0 ${!(isOpen && items.length) && 'd-none'}`}
      style={{
        position: 'absolute',
        top: menuStyle ? `${menuStyle.top}px` : undefined,
        left: menuStyle ? `${menuStyle.left}px` : undefined,
        width: menuStyle ? `${menuStyle.width}px` : undefined,
        maxWidth: menuStyle && menuStyle.maxWidth ? `${menuStyle.maxWidth}px` : undefined,
        boxSizing: 'border-box',
        overflowY: 'auto',
        overflowX: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        zIndex: 1050,
      }}
    >
      {isOpen && items.length > 0 && (
        <>
          {/* Find the index where "Other" section starts */}
          {(() => {
            const recommendedCount = items.filter((m) =>
              recommendedMetricKeys?.some((key) => m.id.toLowerCase() === key.toLowerCase()),
            ).length;
            const hasRecommended = recommendedCount > 0;

            return (
              <>
                {hasRecommended && (
                  <>
                    <li
                      className="py-2 px-3 fw-bold text-muted bg-light"
                      style={{ fontSize: '0.75rem', cursor: 'default' }}
                    >
                      Recommended
                    </li>
                    {items.slice(0, recommendedCount).map((item, index) => (
                      <li
                        key={item.id}
                        {...getItemProps({ item, index })}
                        className={`py-0 px-3 ${highlightedIndex === index ? 'bg-primary text-white' : ''}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="small">{item.title || '(untitled)'}</div>
                      </li>
                    ))}
                  </>
                )}
                {items.length > recommendedCount && (
                  <>
                    {hasRecommended && (
                      <li
                        className="py-2 px-3 fw-bold text-muted bg-light"
                        style={{ fontSize: '0.75rem', cursor: 'default' }}
                      >
                        Other
                      </li>
                    )}
                    {items.slice(recommendedCount).map((item, index) => (
                      <li
                        key={item.id}
                        {...getItemProps({ item, index: index + recommendedCount })}
                        className={`py-0 px-3 ${highlightedIndex === index + recommendedCount ? 'bg-primary text-white' : ''}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="small">{item.title || '(untitled)'}</div>
                      </li>
                    ))}
                  </>
                )}
              </>
            );
          })()}
        </>
      )}
    </ul>
  );

  return (
    <div style={{ flex: '1 1 auto' }} ref={containerRef}>
      <div className="d-flex">
        <input
          className="form-control form-control-sm"
          placeholder="Please enter or select a value"
          {...getInputProps({ id: 'toolbar-metric' })}
        />
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm ms-1 text-danger fw-bold"
          onClick={() => setInputValue('')}
          title="Clear search"
        >
          ✕
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm ms-1"
          {...getToggleButtonProps()}
        >
          {isOpen ? '▲' : '▼'}
        </button>
      </div>

      {typeof document !== 'undefined' && menuStyle ? createPortal(menu, document.body) : menu}
    </div>
  );
}
